(() => {
  'use strict';

  const STORAGE_KEY = 'aonskSeedPlannerPreferencesV2';
  const DAY = 86400000;

  const PROFILE_CLIMATE = {
    tomato: { day: [65, 88], night: [50, 75], soilMin: 60 },
    pepper: { day: [70, 92], night: [55, 78], soilMin: 65 },
    cucumber: { day: [70, 92], night: [55, 78], soilMin: 65 },
    cucurbit: { day: [70, 92], night: [55, 78], soilMin: 65 },
    legume: { day: [55, 88], night: [40, 75], soilMin: 50 },
    corn: { day: [65, 92], night: [50, 78], soilMin: 55 },
    'grain-cover': { day: [45, 90], night: [32, 75], soilMin: 40 },
    brassica: { day: [45, 75], night: [32, 65], soilMin: 40 },
    'leafy-green': { day: [45, 78], night: [32, 68], soilMin: 40 },
    'root-allium': { day: [45, 80], night: [32, 68], soilMin: 40 },
    'warm-vegetable': { day: [68, 92], night: [52, 78], soilMin: 60 },
    vegetable: { day: [50, 88], night: [35, 72], soilMin: 45 },
    flower: { day: [50, 88], night: [35, 72], soilMin: 45 },
    'culinary-herb': { day: [50, 88], night: [35, 72], soilMin: 45 }
  };

  const SOIL_PROFILE = {
    tomato: { ph: [6.0, 6.8], note: 'Use moderate, split fertility and avoid large late nitrogen applications that favor foliage over fruit.' },
    pepper: { ph: [6.0, 6.8], note: 'Use steady fertility and moisture; excessive nitrogen can delay flowering and fruiting.' },
    cucumber: { ph: [6.0, 7.0], note: 'Cucumbers benefit from even moisture and a modest side-dress as vines begin active growth.' },
    cucurbit: { ph: [6.0, 6.8], note: 'Melons, squash, pumpkins, and gourds need warm, well-drained soil; avoid heavy late nitrogen once flowering begins.' },
    legume: { ph: [6.0, 7.0], note: 'Beans and peas usually need less supplemental nitrogen than heavy-feeding crops; follow the report before adding nitrogen.' },
    corn: { ph: [5.8, 7.0], note: 'Corn is nitrogen-demanding, but the rate and timing should follow yield goal, soil organic matter, and regional guidance.' },
    'grain-cover': { ph: [5.8, 7.0], note: 'Match fertility to the stand purpose: harvested grain and a soil-building cover crop do not use the same nitrogen plan.' },
    brassica: { ph: [6.2, 7.2], note: 'Brassicas use substantial nitrogen and sulfur; maintain pH and avoid excess phosphorus where reports are already high.' },
    'leafy-green': { ph: [6.0, 7.0], note: 'Use modest, available nitrogen in split applications and maintain even moisture for quality leaves.' },
    'root-allium': { ph: [6.0, 7.0], note: 'Prioritize loose structure and balanced fertility; excess fresh manure or nitrogen can reduce root or bulb quality.' },
    'perennial-crop': { ph: [6.2, 7.2], note: 'Correct major soil constraints before establishing a permanent bed because later incorporation is difficult.' },
    'culinary-herb': { ph: [6.0, 7.2], note: 'Many herbs perform best with moderate fertility; excessive nitrogen can produce soft growth and dilute aroma.' },
    'traditional-herb': { ph: [5.8, 7.2], note: 'Match fertility and pH to the recorded habitat rather than applying a universal vegetable-garden program.' },
    flower: { ph: [6.0, 7.2], note: 'Use moderate fertility unless the product record or laboratory recommends otherwise.' },
    'native-perennial': { ph: [5.5, 7.5], note: 'Native plants differ widely; habitat match, drainage, and regional ecotype can matter more than high fertility.' },
    default: { ph: [6.0, 7.0], note: 'Use the product record and regional laboratory recommendation to set fertility.' }
  };

  const regionWindows = [
    { prefixes: ['967', '968'], lastFrost: '01-15', firstFrost: '12-15', label: 'Hawaiʻi broad estimate' },
    { prefixes: ['330','331','332','333','334','335','336','337','338','339','340','341','342','344','346','347','349'], lastFrost: '02-01', firstFrost: '12-15', label: 'South Florida broad estimate' },
    { prefixes: ['279'], lastFrost: '03-25', firstFrost: '11-15', label: 'northeastern North Carolina broad estimate' },
    { prefixes: ['270','271','272','273','274','275','276','277','278','280','281','282','283','284','285'], lastFrost: '04-05', firstFrost: '11-01', label: 'North Carolina broad estimate' },
    { prefixes: ['200','201','206','207','208','209','220','221','222','223','224','225','226','227','228','229','230','231','232','233','234','235','236','237','238','239','240','241','242','243','244','245','246'], lastFrost: '04-10', firstFrost: '10-25', label: 'mid-Atlantic broad estimate' },
    { prefixes: ['300','301','302','303','304','305','306','307','308','309','310','311','312','313','314','315','316','317','318','319','320','321','322','323','324','325','326'], lastFrost: '03-20', firstFrost: '11-10', label: 'Southeast broad estimate' },
    { prefixes: ['700','701','703','704','705','706','707','708','710','711','712','713','714','716','717','718','719','720','721','722','723','724','725','726','727','728','729'], lastFrost: '03-15', firstFrost: '11-15', label: 'lower Mississippi Valley broad estimate' },
    { prefixes: ['750','751','752','753','754','755','756','757','758','759','760','761','762','763','764','765','766','767','768','769','770','772','773','774','775','776','777','778','779','780','781','782','783','784','785','786','787','788','789'], lastFrost: '03-10', firstFrost: '11-20', label: 'Texas broad estimate' },
    { prefixes: ['850','851','852','853','855','856','857','859','860','863','864','865'], lastFrost: '02-15', firstFrost: '11-25', label: 'low-desert Southwest broad estimate' },
    { prefixes: ['900','901','902','903','904','905','906','907','908','910','911','912','913','914','915','916','917','918','919','920','921','922','923','924','925','926','927','928'], lastFrost: '02-15', firstFrost: '12-01', label: 'southern California broad estimate' },
    { prefixes: ['970','971','972','973','974'], lastFrost: '04-15', firstFrost: '10-25', label: 'western Oregon broad estimate' },
    { prefixes: ['980','981','982','983','984','985','986'], lastFrost: '04-15', firstFrost: '10-20', label: 'western Washington broad estimate' }
  ];

  function parseJSON(script) {
    try { return JSON.parse(script.textContent); } catch (error) { return {}; }
  }

  function numericRange(value) {
    if (value === null || value === undefined || value === '') return [];
    const matches = String(value).match(/\d+(?:\.\d+)?/g);
    return matches ? matches.map(Number) : [];
  }

  function getMaturity(product) {
    const max = Number(product.maturityMax || 0);
    const min = Number(product.maturityMin || 0);
    if (max) return max;
    if (min) return min;
    const numbers = numericRange(product.maturityText);
    return numbers.length ? Math.max(...numbers) : 0;
  }

  function profileLabel(profile) {
    const labels = {
      tomato: 'tomato', pepper: 'pepper', cucumber: 'cucumber', cucurbit: 'melon, squash, pumpkin, or gourd',
      legume: 'bean or pea', corn: 'corn', 'grain-cover': 'grain or cover crop', brassica: 'brassica',
      'leafy-green': 'leafy green', 'root-allium': 'root or allium crop', 'perennial-crop': 'perennial food crop',
      'culinary-herb': 'culinary herb', 'traditional-herb': 'traditional-use herb', flower: 'flower',
      'native-perennial': 'native or perennial plant', vegetable: 'vegetable'
    };
    return labels[profile] || 'garden seed';
  }

  function productPHRange(product, profile) {
    const recorded = numericRange(product.soilPH);
    if (recorded.length) return [recorded[0], recorded[1] ?? recorded[0]];
    return (SOIL_PROFILE[profile] || SOIL_PROFILE.default).ph;
  }

  function dateFromMonthDay(year, value) {
    const [month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }

  function frostWindow(zip, targetDate) {
    if (!/^\d{5}$/.test(zip || '')) return null;
    const shared = window.AONSK_GARDEN_DATA?.readClimateProfile?.();
    if (shared && shared.zip === zip && shared.lastFrost && shared.firstFrost) {
      const lastParts = shared.lastFrost.split('/');
      const firstParts = shared.firstFrost.split('/');
      const year = targetDate.getFullYear();
      let last = dateFromMonthDay(year, `${lastParts[0]}-${lastParts[1]}`);
      let first = dateFromMonthDay(year, `${firstParts[0]}-${firstParts[1]}`);
      if (targetDate > first) {
        last = dateFromMonthDay(year + 1, `${lastParts[0]}-${lastParts[1]}`);
        first = dateFromMonthDay(year + 1, `${firstParts[0]}-${firstParts[1]}`);
      }
      return {
        lastFrost: shared.lastFrost,
        firstFrost: shared.firstFrost,
        label: shared.sourceLabel || 'saved local climate profile',
        last,
        first,
        daysRemaining: Math.max(0, Math.floor((first - targetDate) / DAY)),
        shared: true
      };
    }
    const prefix = zip.slice(0, 3);
    const match = regionWindows.find((entry) => entry.prefixes.includes(prefix));
    const fallback = { lastFrost: '04-15', firstFrost: '10-15', label: 'continental U.S. broad estimate' };
    const selected = match || fallback;
    const year = targetDate.getFullYear();
    let last = dateFromMonthDay(year, selected.lastFrost);
    let first = dateFromMonthDay(year, selected.firstFrost);
    if (targetDate > first) {
      last = dateFromMonthDay(year + 1, selected.lastFrost);
      first = dateFromMonthDay(year + 1, selected.firstFrost);
    }
    return { ...selected, last, first, daysRemaining: Math.max(0, Math.floor((first - targetDate) / DAY)) };
  }

  function textIncludes(value, terms) {
    const haystack = String(value || '').toLowerCase();
    return terms.some((term) => haystack.includes(term));
  }

  function plainText(value) {
    let output = String(value || '').trim();
    for (let pass = 0; pass < 2 && output; pass += 1) {
      const template = document.createElement('template');
      template.innerHTML = output.replace(/<\s*\/?(?:p|div|li|ul|ol|h[1-6]|br)\b[^>]*>/gi, ' ');
      const decoded = (template.content.textContent || '').trim();
      if (decoded === output) break;
      output = decoded;
    }
    return output.replace(/\s+/g, ' ').trim();
  }

  function addUnique(list, text) {
    const normalized = plainText(text);
    if (normalized && !list.includes(normalized)) list.push(normalized);
  }

  function renderList(element, items, fallback) {
    element.innerHTML = '';
    const values = items.length ? items : [fallback];
    values.forEach((item) => {
      const li = document.createElement('li');
      li.textContent = item;
      element.appendChild(li);
    });
  }

  function getTargetDate(form) {
    const mode = form.elements.planting_mode?.value || 'now';
    const now = new Date();
    if (mode === 'date' && form.elements.planting_date?.value) return new Date(`${form.elements.planting_date.value}T12:00:00`);
    if (mode === 'next-year') return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate(), 12, 0, 0, 0);
    return now;
  }

  function evaluate(product, values) {
    const fit = [];
    const plan = [];
    const alternative = [];
    let score = 0;
    const profile = product.profile || 'generic-seed';
    const maturity = getMaturity(product);
    const targetDate = getTargetDate(values.form);
    const zip = values.zip;
    const window = frostWindow(zip, targetDate);
    const fullSeason = values.planting_mode === 'full-season';

    if (window) {
      addUnique(plan, `The ZIP estimate uses a ${window.label} with an approximate first frost near ${window.first.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.`);
      if (!fullSeason && maturity) {
        const buffer = 14;
        if (window.daysRemaining >= maturity + buffer) {
          addUnique(fit, `The broad seasonal estimate leaves about ${window.daysRemaining} days before first frost, compared with roughly ${maturity} listed days to maturity.`);
          score += 2;
        } else if (window.daysRemaining >= maturity) {
          addUnique(plan, `The estimated window is close to the listed maturity. Weather delays could make the crop tight without protection.`);
          score -= 1;
        } else {
          addUnique(alternative, `Only about ${window.daysRemaining} frost-free days remain in the broad estimate, less than the listed maturity. An earlier variety or protected growing may be easier.`);
          score -= 3;
        }
      }
    } else if (zip) {
      addUnique(plan, 'The ZIP could not be matched to a specific regional window, so use local frost dates and current conditions before planting.');
    }

    const climate = PROFILE_CLIMATE[profile];
    const dayTemp = Number(values.day_temp || 0);
    const nightTemp = Number(values.night_temp || 0);
    const soilTemp = Number(values.soil_temp || 0);
    const germinationTemperatures = numericRange(product.germinationTemperature);
    const recordedSoilMinimum = germinationTemperatures.length ? Math.min(...germinationTemperatures) : 0;
    if (climate && dayTemp) {
      if (dayTemp >= climate.day[0] && dayTemp <= climate.day[1]) { addUnique(fit, `${dayTemp}°F days are within a broadly useful range for this ${profileLabel(profile)}.`); score += 0.5; }
      else if (dayTemp < climate.day[0]) { addUnique(plan, `${dayTemp}°F days may slow this ${profileLabel(profile)}; compare the selected date with the recorded planting season.`); score -= 0.5; }
      else { addUnique(plan, `${dayTemp}°F days can add heat stress; use mulch, timely irrigation, shade where appropriate, or a cooler planting window.`); score -= 0.5; }
    }
    if (climate && nightTemp) {
      if (nightTemp >= climate.night[0] && nightTemp <= climate.night[1]) { addUnique(fit, `${nightTemp}°F nights are within a broadly workable range for this crop profile.`); score += 0.5; }
      else if (nightTemp < climate.night[0]) { addUnique(plan, `${nightTemp}°F nights may slow establishment or damage a tender crop; confirm frost risk and hardening.`); score -= 1; }
      else { addUnique(plan, `${nightTemp}°F nights may reduce recovery from daytime heat for this crop.`); score -= 0.5; }
    }
    if (soilTemp) {
      const minimum = recordedSoilMinimum || climate?.soilMin || 0;
      if (minimum && soilTemp >= minimum) { addUnique(fit, `The measured ${soilTemp}°F seed-zone temperature meets the recorded or profile starting minimum near ${minimum}°F.`); score += 1; }
      else if (minimum) { addUnique(alternative, `The measured ${soilTemp}°F seed zone is below the recorded or profile starting point near ${minimum}°F.`); score -= 2; }
    }
    if (values.protection === 'light' || values.protection === 'greenhouse') addUnique(fit, 'Season protection adds flexibility when it is ventilated and managed for overheating.');
    if (values.exposure === 'humid') addUnique(plan, 'A humid or slow-drying site needs wider spacing, morning watering, and airflow to reduce foliar disease pressure.');
    if (values.exposure === 'windy') addUnique(plan, 'An exposed site may need wind protection or support without blocking airflow.');
    if (values.exposure === 'heat-trap') addUnique(plan, 'Reflected heat can raise root-zone and leaf temperatures above the general forecast.');

    const sunText = String(product.sunlight || '').toLowerCase();
    const selectedSun = Number(values.sun || 0);
    if (textIncludes(sunText, ['full sun', '6-8', '6 to 8', '8 hours'])) {
      if (selectedSun >= 6) { addUnique(fit, 'Your selected site meets the product’s full-sun guidance.'); score += 1; }
      else { addUnique(alternative, 'This crop is listed for full sun, while your site has fewer than six direct-sun hours.'); score -= 2; }
    } else if (selectedSun >= 4) {
      addUnique(fit, 'The selected light level is workable for many garden seeds, subject to the product’s listed sunlight guidance.');
    }

    if (values.space === 'container') {
      if (product.containerSuitable === true || textIncludes(product.containerSuitable, ['true', 'yes'])) {
        addUnique(fit, 'This product is recorded as container suitable.'); score += 1;
      } else if (product.containerSuitable === false || textIncludes(product.containerSuitable, ['false', 'no'])) {
        addUnique(alternative, 'This product is recorded as unsuitable for containers. An in-ground or raised-bed variety may be easier.'); score -= 2;
      } else if (['corn', 'grain-cover', 'cucurbit', 'perennial-crop'].includes(profile)) {
        addUnique(alternative, 'This crop usually needs more root room, pollination space, or vine space than a typical container provides.'); score -= 2;
      } else {
        addUnique(plan, 'Container suitability is not recorded. Check mature height, spread, and root-room needs in the complete facts.');
      }
    }

    if (values.drainage === 'wet') {
      addUnique(plan, 'Improve drainage or use a raised bed; prolonged saturation can reduce germination and root health.'); score -= 1;
    }
    if (values.drainage === 'fast' || values.soil === 'sandy') {
      addUnique(plan, 'Fast-draining soil may need more frequent moisture checks and organic matter.');
    }
    if (values.soil === 'clay' && ['root-allium', 'cucurbit'].includes(profile)) {
      addUnique(plan, 'Heavy clay can restrict roots or drainage. Loosen the bed and follow the product’s spacing and soil guidance.'); score -= 1;
    }
    if (values.watering === 'limited' && ['tomato','pepper','cucumber','cucurbit','leafy-green'].includes(profile)) {
      addUnique(plan, 'This crop benefits from steadier root-zone moisture than once-weekly checking usually provides. Mulch or irrigation may help.'); score -= 1;
    }
    if (values.watering_method === 'drip' || values.watering_method === 'hand') addUnique(fit, 'Watering at soil level helps keep foliage drier while directing moisture to the root zone.');
    if (values.watering_method === 'overhead' && ['tomato','pepper','cucumber','cucurbit','brassica'].includes(profile)) addUnique(plan, 'If watering overhead, irrigate early enough for foliage to dry before night.');
    if (values.watering_method === 'rainfall' && textIncludes(product.water, ['moderate','regular','consistent','high'])) { addUnique(plan, 'The product record calls for steady moisture, so rainfall alone may need a backup plan.'); score -= 1; }
    if (values.mulch === 'yes') addUnique(fit, 'A correctly timed mulch can reduce evaporation and soil splash after the root zone reaches a suitable temperature.');
    if (values.space_capacity === 'tight') { addUnique(plan, 'Tighter-than-listed spacing can reduce airflow, root room, pollination access, or harvest quality.'); score -= 1; }

    const ph = Number(values.soil_ph || 0);
    if (ph) {
      const [phMin, phMax] = productPHRange(product, profile);
      if (ph >= phMin && ph <= phMax) { addUnique(fit, `The reported pH ${ph.toFixed(1)} is within the product target of about ${phMin}–${phMax}.`); score += 1; }
      else { addUnique(plan, `The reported pH ${ph.toFixed(1)} is outside the product target of about ${phMin}–${phMax}. Use the laboratory lime or sulfur recommendation rather than guessing a rate.`); score -= 1; }
    } else if (values.soil_test === 'have') addUnique(plan, 'Enter the pH from the report or open the soil interpreter below to compare the full set of values.');
    else if (values.soil_test === 'none') addUnique(plan, 'A laboratory soil test is the best next step before making major pH or nutrient corrections.');

    if (values.rotation === 'recent') addUnique(plan, 'A recently repeated crop family can increase carryover pest and disease pressure; rotate where practical.');
    if (values.rotation === 'disease') { addUnique(alternative, 'Recurring disease in the same crop family makes a different bed, fresh container mix, or a longer rotation easier.'); score -= 2; }

    if (product.stratification) {
      addUnique(plan, `Recorded stratification guidance: ${product.stratification}.`);
    }
    if (product.scarification) addUnique(plan, `Recorded scarification guidance: ${product.scarification}.`);
    if (product.soaking) addUnique(plan, `Recorded pre-soaking guidance: ${product.soaking}.`);

    const experienceRank = Number(product.experienceRank || 0);
    const germinationRank = Number(product.germinationRank || 0);
    const prepEntries = [product.stratification, product.scarification, product.soaking, product.germinationLightRequirement]
      .map((value) => String(value || '').trim())
      .filter((value) => value && !/(none required|not required|either|not specified)/i.test(value));
    const germinationNumbers = numericRange(product.germination);
    const germinationMaximum = germinationNumbers.length ? Math.max(...germinationNumbers) : 0;

    if (product.experienceLabel) addUnique(plan, `Recorded gardener-experience profile: ${product.experienceLabel}.`);
    if (product.germinationDifficulty) addUnique(plan, `Recorded germination profile: ${product.germinationDifficulty}.`);
    if (product.experienceReason) addUnique(plan, product.experienceReason);

    if (values.experience === 'beginner' && experienceRank >= 4) {
      addUnique(alternative, `This product is reviewed at experience level ${experienceRank} of 5. A lower-preparation seed may be easier for a first season.`);
      score -= 2;
    } else if (values.experience === 'beginner' && experienceRank > 0 && experienceRank <= 2) {
      addUnique(fit, 'The reviewed experience profile is beginner-friendly or requires only one extra step.');
      score += 1;
    }

    if (values.prep_comfort === 'no-prep' && prepEntries.length) {
      addUnique(alternative, `This seed has recorded preparation beyond ordinary sowing: ${prepEntries.join(' · ')}.`);
      score -= 2;
    } else if (values.prep_comfort === 'one-step' && prepEntries.length > 1) {
      addUnique(alternative, 'This seed has more than one recorded preparation requirement. A one-step or untreated seed may be easier.');
      score -= 1;
    } else if (values.prep_comfort !== 'no-prep' && prepEntries.length) {
      addUnique(fit, 'Your preparation preference allows for the treatment recorded on this product.');
      score += 1;
    }

    if (values.wait_tolerance === 'fast' && ((germinationRank >= 3) || germinationMaximum > 14)) {
      addUnique(alternative, 'This seed may take longer than your preference for emergence within about two weeks.');
      score -= 2;
    } else if (values.wait_tolerance === 'weeks' && ((germinationRank >= 5) || germinationMaximum > 45)) {
      addUnique(alternative, 'The reviewed germination window may extend beyond several weeks.');
      score -= 1;
    } else if (germinationMaximum && values.wait_tolerance !== 'fast') {
      addUnique(fit, `Your patience preference can accommodate the recorded germination window of ${product.germination}.`);
    }

    if (values.seed_start_preference === 'direct') {
      if (/high|sensitive|disturb/i.test(String(product.transplantSensitivity || ''))) {
        addUnique(fit, 'Your direct-sowing preference avoids the recorded transplant sensitivity.');
        score += 1;
      } else if (['pepper', 'tomato'].includes(profile)) {
        addUnique(alternative, 'This warm-season crop is commonly easier when started indoors or planted as a transplant.');
        score -= 1;
      }
    }
    if (values.starting_equipment === 'basic' && recordedSoilMinimum >= 70) addUnique(plan, `This seed is recorded for a warm germination range; a basic indoor setup may need a measured seed-zone heat source.`);
    if (values.starting_equipment === 'outdoor' && ['tomato','pepper','warm-vegetable'].includes(profile)) { addUnique(alternative, 'Outdoor-only starting can be difficult for a long-season tender crop where soil warms late.'); score -= 1; }
    if (product.growingComplexity) addUnique(plan, `Recorded growing challenge: ${product.growingComplexity}.`);
    if (product.firstHarvestOrBloom) addUnique(plan, `Recorded timing expectation: ${product.firstHarvestOrBloom}.`);
    if (product.seedStartingSuccessTip) addUnique(plan, `Best chance of success: ${product.seedStartingSuccessTip}`);

    switch (profile) {
      case 'tomato':
        if (values.start_method === 'direct') { addUnique(alternative, 'Tomatoes are usually more dependable when started indoors or planted as hardened transplants after nights and soil warm.'); score -= 2; }
        else { addUnique(fit, 'Your indoor-start or transplant plan matches common tomato practice.'); score += 1; }
        if (textIncludes(product.growthHabit, ['indeterminate','vining']) || textIncludes(product.support, ['heavy','trellis','cage','stake'])) {
          if (values.support === 'none') { addUnique(alternative, 'This tomato needs dependable support, but none is planned.'); score -= 2; }
          else { addUnique(fit, 'Your support plan matches the recorded mature habit.'); score += 1; }
        }
        if (values.protection !== 'none') addUnique(fit, 'Season protection adds flexibility for a long-season tomato, provided plants still receive enough light and airflow.');
        break;
      case 'pepper':
        if (values.start_method === 'direct') { addUnique(alternative, 'Peppers are usually easier when started indoors or planted as transplants after the soil warms.'); score -= 2; }
        else { addUnique(fit, 'Your indoor-start or transplant plan matches common pepper practice.'); score += 1; }
        if (textIncludes(product.support, ['stake','cage','support']) && values.support === 'none') addUnique(plan, 'The product record calls for support; install it before stems carry a heavy fruit load.');
        if (values.protection !== 'none') addUnique(fit, 'Season protection gives a warm-season pepper more flexibility.');
        break;
      case 'cucumber':
      case 'cucurbit':
        if (values.vine_space === 'limited') { addUnique(alternative, 'The available vine space may be tight unless the variety is compact or can be trellised.'); score -= 2; }
        else addUnique(fit, 'Your vine-space plan supports this crop’s spreading or climbing growth.');
        if (values.start_method === 'transplant') addUnique(plan, 'Cucurbits can resent root disturbance. Transplant young plants gently without breaking the root ball.');
        if (values.vine_space === 'trellis' && values.support === 'none') { addUnique(alternative, 'A vertical vine plan needs a strong trellis and, for heavier fruit, slings or another load-bearing plan.'); score -= 1; }
        if (values.pollination === 'limited') addUnique(plan, 'Limited pollinator access can reduce fruit set; grow compatible flowers, avoid spraying open blooms, or learn hand pollination.');
        if (values.pollination === 'good' || values.pollination === 'managed') addUnique(fit, 'Your pollination plan supports fruit set for a flowering cucurbit.');
        break;
      case 'legume':
        if (textIncludes(product.growthHabit, ['pole', 'runner', 'climbing']) || textIncludes(product.support, ['trellis', 'pole', 'support'])) {
          if (values.support === 'yes') { addUnique(fit, 'You have support available for a climbing habit.'); score += 1; }
          else { addUnique(alternative, 'This product appears to need support, but none is available. A bush variety may be easier.'); score -= 2; }
        }
        addUnique(fit, 'Beans and peas are commonly direct sown once soil conditions fit the crop.');
        if (values.nitrogen_plan === 'heavy') addUnique(plan, 'Heavy nitrogen can produce excessive vine growth and may reduce nodulation or pod set; use a soil-test-based rate.');
        break;
      case 'corn':
        if (Number(values.corn_rows) >= 4 && Number(values.corn_plants) >= 16) { addUnique(fit, 'Several short rows and at least 16 plants provide a stronger starting point for wind pollination.'); score += 2; }
        else { addUnique(alternative, 'A small number of isolated corn plants often pollinates poorly. Use a larger block or plan careful hand pollination.'); score -= 3; }
        if (values.isolation === 'no') addUnique(plan, 'If saving seed or preserving type, separate other corn by distance or flowering time.');
        break;
      case 'grain-cover':
        if (values.space === 'container') { addUnique(alternative, 'Grains and cover crops generally need a bed, plot, or field rather than a typical container.'); score -= 2; }
        if (values.planting_scale === 'plot' || values.planting_scale === 'field') { addUnique(fit, 'Your planting scale gives this stand more room for useful coverage or harvest.'); score += 1; }
        if (values.use === 'cover' && values.termination === 'unsure') addUnique(plan, 'Decide whether to mow, cut, or incorporate the stand before mature seed so it does not reseed unexpectedly.');
        if (values.use === 'grain' && values.termination !== 'harvest') addUnique(plan, 'For grain or seed harvest, allow the crop to mature and plan for drying, threshing, and cleaning.');
        break;
      case 'brassica':
        if (values.season_preference === 'cool') { addUnique(fit, 'Your cool-season plan matches the usual preference of this crop group.'); score += 2; }
        else { addUnique(alternative, 'Warm weather can increase bolting, bitterness, pests, or poor head formation in many cool-season crops.'); score -= 2; }
        if (values.pest_protection === 'ready') addUnique(fit, 'A row-cover or monitoring plan addresses common early brassica pest pressure.');
        else if (values.pest_protection === 'none') addUnique(plan, 'Inspect frequently for caterpillars, flea beetles, aphids, and root pests; use insect netting before damage begins where appropriate.');
        break;
      case 'leafy-green':
        if (values.season_preference === 'cool') { addUnique(fit, 'Your cool-season plan supports leaf quality and slower bolting.'); score += 2; }
        else { addUnique(plan, 'Warm weather can accelerate bolting and bitterness; afternoon shade and short successions may help.'); score -= 1; }
        if (values.succession === 'yes') addUnique(fit, 'Repeat sowings can spread the harvest and replace plants that bolt.');
        break;
      case 'root-allium':
        if (values.root_bed === 'deep-loose') { addUnique(fit, 'Loose, deep soil supports straighter roots and easier sizing.'); score += 2; }
        else if (values.root_bed === 'heavy') { addUnique(plan, 'Compacted soil can limit root shape and size; loosen the bed or choose a shorter-rooted type.'); score -= 2; }
        if (Number(values.soil_depth) && Number(values.soil_depth) < 8) addUnique(plan, 'Less than about 8 inches of loose soil can restrict many full-size roots; compare the mature root form in the product record.');
        if (values.thinning === 'no') addUnique(plan, 'Many root crops require thinning for proper spacing. Pelleted seed, careful sowing, or transplantable alliums may be easier.');
        break;
      case 'perennial-crop':
        if (values.permanent_bed === 'yes') { addUnique(fit, 'A permanent bed supports the long establishment and repeated harvest expected from this crop.'); score += 2; }
        else { addUnique(alternative, 'This crop needs long-term bed space; a temporary or frequently moved garden may be a difficult fit.'); score -= 2; }
        if (values.patience === 'years') { addUnique(fit, 'Your willingness to wait through establishment fits a perennial food crop.'); score += 1; }
        else { addUnique(alternative, 'A full harvest may take more than one season. An annual crop may be easier if you need first-year production.'); score -= 2; }
        if (values.start_method === 'direct') addUnique(plan, 'Direct sowing may extend establishment time; follow the product-specific starting instructions carefully.');
        if (values.winter_drainage === 'wet') { addUnique(alternative, 'A waterlogged winter root zone can threaten a permanent planting; improve drainage or choose another site before establishment.'); score -= 2; }
        break;
      case 'culinary-herb':
        if (values.space === 'container') addUnique(fit, 'Many culinary herbs adapt well to containers when drainage and mature size are considered.');
        if (values.permanent === 'perennial' && !textIncludes(product.lifeCycle, ['perennial'])) addUnique(plan, 'This product is not recorded as perennial, so plan for seasonal replanting.');
        break;
      case 'traditional-herb':
      case 'native-perennial':
        if ((product.stratification || textIncludes(product.lifeCycle, ['perennial'])) && values.prep === 'simple') {
          addUnique(alternative, 'This seed may require dormancy preparation or a longer establishment period than your preference for simple, quick sowing.'); score -= 2;
        }
        if (values.patience === 'months') { addUnique(fit, 'Your willingness to allow dormancy and gradual establishment fits many native and perennial seeds.'); score += 1; }
        if (values.habitat_match === 'different') addUnique(plan, 'The proposed site differs from the recorded habitat; verify light, moisture, drainage, and native-range context before planting.');
        break;
      case 'flower':
        if (values.bloom === 'first-year' && textIncludes(product.lifeCycle, ['biennial', 'perennial'])) {
          addUnique(alternative, 'This life cycle may not reliably bloom in the first season. An annual flower may fit your goal better.'); score -= 2;
        }
        if (values.use === 'pollinator') addUnique(fit, 'Your pollinator goal can be checked against the product’s bloom season, life cycle, and habitat information.');
        break;
      case 'warm-fruiting':
      case 'warm-vegetable':
        if (values.start_method === 'direct') { addUnique(plan, 'Warm-season fruiting crops are often more dependable when started indoors or planted as transplants where the season is short.'); score -= 1; }
        else { addUnique(fit, 'Your indoor-start or transplant plan protects early growth while the soil and nights warm.'); score += 1; }
        if (values.season_length === 'short') { addUnique(alternative, 'A short or cool season may not provide enough time for full production without protection or an earlier variety.'); score -= 2; }
        break;
      case 'cool-vegetable':
        if (values.season_preference === 'cool') { addUnique(fit, 'Your cool-season plan matches the normal preference of this crop group.'); score += 2; }
        else { addUnique(plan, 'Warm conditions can slow establishment, reduce quality, or push some cool-season vegetables toward bolting.'); score -= 1; }
        break;
      default:
        addUnique(plan, 'Use the complete facts to confirm sowing method, spacing, water, and season before planting.');
    }

    if (values.experience === 'beginner') {
      if (score >= 1) addUnique(fit, 'The current plan appears manageable for a beginner who follows the complete sowing and care instructions.');
      if (score <= -2) addUnique(alternative, 'A faster, more compact, or less specialized variety may provide an easier first-season experience.');
    }

    let title = 'A workable match with a few planning notes';
    let label = 'Garden-fit result';
    if (score >= 3) { title = 'This looks like a strong match'; label = 'Strong match'; }
    if (score <= -3) { title = 'This may be a challenging match right now'; label = 'Consider an alternative'; }

    return {
      fit,
      plan,
      alternative,
      title,
      label,
      summary: `The result compares your answers with the recorded facts and normal needs of this ${profile.replace(/-/g, ' ')} profile.`
    };
  }

  function initFramework(root) {
    const form = root.querySelector('[data-seed-fit-form]');
    if (!form) return;
    const productScript = root.querySelector('[data-seed-fit-product]');
    const product = parseJSON(productScript);
    const result = root.querySelector('[data-seed-fit-result]');
    const dateField = root.querySelector('[data-date-field]');
    const climateSummary = root.querySelector('[data-fit-climate-summary]');

    try {
      const saved = {
        ...(window.AONSK_GARDEN_DATA?.readProfilePreferences?.('productGardenFit') || {}),
        ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
      };
      Object.entries(saved).forEach(([name, value]) => {
        if (form.elements[name] && value !== undefined && value !== null) form.elements[name].value = value;
      });
    } catch (error) { /* ignore invalid saved preferences */ }
    const sharedClimate = window.AONSK_GARDEN_DATA?.readClimateProfile?.();
    if (form.elements.zip && !form.elements.zip.value && sharedClimate?.zip) form.elements.zip.value = sharedClimate.zip;

    const updateDate = () => {
      if (dateField) dateField.hidden = form.elements.planting_mode?.value !== 'date';
    };
    const updateClimate = () => {
      if (!climateSummary) return;
      const zip = String(form.elements.zip?.value || '').trim();
      if (!zip) {
        climateSummary.innerHTML = '<strong>Add an optional ZIP code and planting date for a broad frost-window check.</strong><span>Use local forecasts and measured soil temperature for final planting decisions.</span>';
        return;
      }
      if (!/^\d{5}$/.test(zip)) {
        climateSummary.innerHTML = '<strong>Enter a five-digit US ZIP code.</strong><span>Or leave the field blank and complete the rest of the planner.</span>';
        return;
      }
      const target = getTargetDate(form);
      const estimate = frostWindow(zip, target);
      climateSummary.innerHTML = `<strong>${estimate.label}: about ${estimate.last.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} to ${estimate.first.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.</strong><span>${estimate.daysRemaining} estimated frost-free days remain from the selected date. ${estimate.shared?'This uses the saved local climate profile; confirm current forecasts before planting.':'This is a broad regional estimate, not live weather.'}</span>`;
    };
    updateDate();
    updateClimate();
    form.elements.planting_mode?.addEventListener('change', updateDate);
    form.elements.planting_mode?.addEventListener('change', updateClimate);
    form.elements.planting_date?.addEventListener('change', updateClimate);
    form.elements.zip?.addEventListener('input', updateClimate);

    form.addEventListener('reset', () => {
      setTimeout(() => {
        updateDate();
        updateClimate();
        if (result) result.hidden = true;
      }, 0);
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const values = Object.fromEntries(new FormData(form).entries());
      values.form = form;
      const zip = String(values.zip || '').trim();
      if (zip && !/^\d{5}$/.test(zip)) {
        form.elements.zip.setCustomValidity('Enter a five-digit US ZIP code or leave it blank.');
        form.elements.zip.reportValidity();
        return;
      }
      form.elements.zip?.setCustomValidity('');
      values.zip = zip;

      const output = evaluate(product, values);
      root.querySelector('[data-result-label]').textContent = output.label;
      root.querySelector('[data-result-title]').textContent = output.title;
      root.querySelector('[data-result-summary]').textContent = output.summary;
      renderList(root.querySelector('[data-result-fit]'), output.fit, 'No strong advantage was identified from the recorded product facts and current answers.');
      renderList(root.querySelector('[data-result-plan]'), output.plan, 'Review the complete growing facts and local conditions before planting.');
      renderList(root.querySelector('[data-result-alternative]'), output.alternative, 'No clear reason to choose another variety was identified.');
      result.hidden = false;
      result.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const save = { ...values };
      delete save.form;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(save)); } catch (error) { /* storage unavailable */ }
      window.AONSK_GARDEN_DATA?.updateProfilePreferences?.('productGardenFit', save, {
        sourceName: 'Product-page garden-fit questionnaire',
        sourceVersion: '11.72'
      });
    });
  }

  const P_TARGETS = {
    mehlich3: { name: 'Mehlich-3', range: [30, 60] },
    mehlich1: { name: 'Mehlich-1', range: [20, 40] },
    bray1: { name: 'Bray P1', range: [25, 50] },
    olsen: { name: 'Olsen', range: [12, 25] },
    morgan: { name: 'Morgan', range: [10, 20] }
  };

  function soilNumber(form, name) {
    const value = Number(form.elements[name]?.value);
    return form.elements[name]?.value === '' || !Number.isFinite(value) ? null : value;
  }

  function soilList(items) {
    return `<ul>${items.map((item) => `<li class="is-${item.state}">${item.text}</li>`).join('')}</ul>`;
  }

  function initSoil(root) {
    const form = root.querySelector('[data-soil-form]');
    const result = root.querySelector('[data-soil-result]');
    if (!form || !result || root.dataset.soilReady === 'true') return;
    root.dataset.soilReady = 'true';
    const profile = root.dataset.profile || 'default';
    const config = SOIL_PROFILE[profile] || SOIL_PROFILE.default;
    const savedSoil = window.AONSK_GARDEN_DATA?.readLatestSoilReport?.();
    if (savedSoil?.data) {
      Object.entries(savedSoil.data).forEach(([name, value]) => {
        const control = form.elements[name];
        if (control && (control.value === '' || control.value === 'unknown') && value !== null && value !== undefined) control.value = value;
      });
      const savedNote = root.querySelector('[data-soil-saved-note]');
      if (savedNote) savedNote.hidden = false;
    }
    const recordedPH = numericRange(root.dataset.targetPh);
    const targetPH = recordedPH.length ? [recordedPH[0], recordedPH[1] ?? recordedPH[0]] : config.ph;
    const targetCopy = root.querySelector('[data-soil-ph-target]');
    if (targetCopy) targetCopy.textContent = `Product target: about ${targetPH[0]}–${targetPH[1]}`;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const summary = [];
      const actions = [];
      const ph = soilNumber(form, 'ph');
      const organicMatter = soilNumber(form, 'organic_matter');
      const pPpm = soilNumber(form, 'p_ppm');
      const kPpm = soilNumber(form, 'k_ppm');
      const calcium = soilNumber(form, 'calcium');
      const magnesium = soilNumber(form, 'magnesium');
      const cec = soilNumber(form, 'cec');
      const ec = soilNumber(form, 'ec');
      const nitrate = soilNumber(form, 'nitrate');
      const limeRate = soilNumber(form, 'lime_rate');
      const area = soilNumber(form, 'area') || 100;
      const texture = form.elements.texture.value;
      const drainage = form.elements.drainage.value;
      let pRating = form.elements.p_rating.value;
      let kRating = form.elements.k_rating.value;
      const extractant = form.elements.extractant.value;
      let entered = [ph, organicMatter, pPpm, kPpm, calcium, magnesium, cec, ec, nitrate, limeRate].some((value) => value !== null) || pRating || kRating || texture !== 'unknown' || drainage !== 'unknown';

      if (!entered) {
        result.querySelector('[data-soil-summary]').innerHTML = '<h4>Enter at least one report or site value</h4><p>Start with pH, a laboratory nutrient rating, texture, or drainage.</p>';
        result.querySelector('[data-soil-actions]').innerHTML = '';
        result.hidden = false;
        return;
      }

      if (ph !== null) {
        if (ph < targetPH[0]) {
          summary.push({ state: 'warn', text: `pH ${ph.toFixed(1)} is below the product target near ${targetPH[0]}–${targetPH[1]}.` });
          actions.push({ state: 'warn', text: 'Use the laboratory lime requirement; soil texture and buffering capacity make a guessed lime rate unreliable.' });
        } else if (ph > targetPH[1]) {
          summary.push({ state: 'warn', text: `pH ${ph.toFixed(1)} is above the product target near ${targetPH[0]}–${targetPH[1]}.` });
          actions.push({ state: 'warn', text: 'Avoid lime and wood ash. Confirm a regional acidification recommendation before applying sulfur.' });
        } else summary.push({ state: 'good', text: `pH ${ph.toFixed(1)} is within the product target near ${targetPH[0]}–${targetPH[1]}.` });
      } else actions.push({ state: 'warn', text: `Enter pH when available; the product target is about ${targetPH[0]}–${targetPH[1]}.` });

      if (limeRate !== null) {
        const scaled = limeRate * area / 1000;
        actions.push({ state: 'good', text: `The entered laboratory lime rate scales to ${scaled.toFixed(1)} lb for ${area.toLocaleString()} sq ft. Preserve the lab's material type, timing, and incorporation instructions.` });
      }

      if (organicMatter !== null) {
        if (organicMatter < 3) {
          summary.push({ state: 'warn', text: `Organic matter is ${organicMatter.toFixed(1)}%, a relatively low garden-soil reading.` });
          actions.push({ state: 'good', text: 'Build organic matter gradually with tested compost, cover crops, roots, and mulch; account for nutrients in compost or manure.' });
        } else if (organicMatter > 10) {
          summary.push({ state: 'warn', text: `Organic matter is ${organicMatter.toFixed(1)}%, a high reading for many mineral soils.` });
          actions.push({ state: 'warn', text: 'Avoid automatically adding manure or phosphorus-rich compost; high organic matter can already supply substantial nutrients.' });
        } else summary.push({ state: 'good', text: `Organic matter ${organicMatter.toFixed(1)}% is in a broadly workable garden-soil range.` });
      }

      if (!pRating && pPpm !== null && P_TARGETS[extractant]) {
        const range = P_TARGETS[extractant].range;
        pRating = pPpm < range[0] ? 'low' : (pPpm > range[1] * 1.5 ? 'high' : 'optimum');
        summary.push({ state: pRating === 'optimum' ? 'good' : 'warn', text: `Phosphorus ${pPpm} ppm screens as ${pRating} against the ${P_TARGETS[extractant].name} reference range ${range[0]}–${range[1]} ppm.` });
      } else if (!pRating && pPpm !== null) {
        actions.push({ state: 'warn', text: `Phosphorus is ${pPpm} ppm, but no calibrated extractant was selected. Use the report's own rating rather than comparing ppm across methods.` });
      }
      if (pRating === 'very-low' || pRating === 'low') actions.push({ state: 'warn', text: `Phosphorus is rated ${pRating.replace('-', ' ')}. Use the report's crop-specific P₂O₅ rate and account for compost or blended fertilizer.` });
      if (pRating === 'optimum') summary.push({ state: 'good', text: 'Phosphorus is rated sufficient; routine buildup is usually unnecessary.' });
      if (pRating === 'high' || pRating === 'very-high') actions.push({ state: 'stop', text: `Phosphorus is rated ${pRating.replace('-', ' ')}. Avoid phosphorus fertilizer and manure unless a specialist directs otherwise.` });

      if (!kRating && kPpm !== null) {
        const low = cec !== null && cec < 8 ? 90 : 120;
        const high = cec !== null && cec > 20 ? 300 : 250;
        kRating = kPpm < low ? 'low' : (kPpm > high ? 'high' : 'optimum');
        summary.push({ state: kRating === 'optimum' ? 'good' : 'warn', text: `Potassium ${kPpm} ppm gives a broad ${kRating} screen; use the laboratory rating because CEC and method change interpretation.` });
      }
      if (kRating === 'very-low' || kRating === 'low') actions.push({ state: 'warn', text: `Potassium is rated ${kRating.replace('-', ' ')}. Follow the report's K₂O rate; sandy or low-CEC soil may benefit from split applications.` });
      if (kRating === 'optimum') summary.push({ state: 'good', text: 'Potassium is rated sufficient; a maintenance or zero rate may be appropriate.' });
      if (kRating === 'high' || kRating === 'very-high') actions.push({ state: 'stop', text: `Potassium is rated ${kRating.replace('-', ' ')}. Avoid extra potash and review calcium/magnesium balance with the laboratory.` });

      if (calcium !== null || magnesium !== null) {
        const values = [calcium !== null ? `calcium ${calcium} ppm` : '', magnesium !== null ? `magnesium ${magnesium} ppm` : ''].filter(Boolean).join(' and ');
        summary.push({ state: 'good', text: `The report lists ${values}. Interpret sufficiency with pH, CEC, saturation, and the laboratory's regional rating.` });
      }
      if (cec !== null) {
        if (cec < 8) actions.push({ state: 'warn', text: `CEC ${cec} indicates limited nutrient-holding capacity; smaller split nutrient applications and careful irrigation are often useful.` });
        else summary.push({ state: 'good', text: `CEC is ${cec} meq/100g; use it to interpret nutrient retention, not as a standalone fertility target.` });
      }
      if (ec !== null) {
        if (ec >= 4) actions.push({ state: 'stop', text: `EC ${ec} dS/m indicates serious soluble-salt risk for many garden crops. Confirm units and obtain a crop- and soil-specific leaching or remediation plan.` });
        else if (ec >= 2) actions.push({ state: 'warn', text: `EC ${ec} dS/m may stress salt-sensitive seedlings and crops. Confirm units, irrigation-water quality, and drainage.` });
        else summary.push({ state: 'good', text: `EC ${ec} dS/m does not show an obvious broad salt warning, subject to the crop and laboratory method.` });
      }
      if (nitrate !== null) {
        summary.push({ state: 'good', text: `Nitrate-N is ${nitrate} ppm; credit this existing nitrogen before choosing a fertilizer rate.` });
        if (profile === 'legume' && nitrate > 25) actions.push({ state: 'warn', text: 'For a bean or pea, high available nitrogen can suppress nodulation or favor foliage; avoid routine high-N feeding.' });
      }

      if (drainage === 'slow') actions.push({ state: 'stop', text: 'Slow drainage or ponding is a root-disease risk. Correct the physical site problem before adding fertilizer.' });
      if (drainage === 'fast' || texture === 'sand') actions.push({ state: 'warn', text: 'Fast-draining soil benefits from smaller irrigation and nutrient doses, mulch, and gradual organic-matter improvement.' });
      if (texture === 'clay') actions.push({ state: 'warn', text: 'For clay soil, protect structure: avoid working it wet, prevent compaction, and solve drainage before deep amendment.' });
      if (texture === 'container') actions.push({ state: 'warn', text: 'Container and manufactured mixes are better managed with a media test and fertilizer program designed for soilless substrates.' });

      actions.push({ state: 'good', text: config.note });
      result.querySelector('[data-soil-summary]').innerHTML = `<h4>Report summary</h4>${soilList(summary.length ? summary : [{ state: 'good', text: 'No major issue was identified from the values entered.' }])}`;
      result.querySelector('[data-soil-actions]').innerHTML = `<h4>Prioritized next steps</h4>${soilList(actions)}`;
      result.hidden = false;
      result.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const reportValues = Object.fromEntries(new FormData(form).entries());
      window.AONSK_GARDEN_DATA?.writeSoilReport?.(reportValues, {
        sourceName: 'Product-page soil interpreter',
        sourceVersion: '11.72',
        productContext: {
          id: root.dataset.productId || null,
          handle: root.dataset.productHandle || null,
          title: root.dataset.productTitle || null,
          profile
        },
        assumptions: ['Values were entered or confirmed by the customer. Laboratory method and regional calibration remain authoritative.'],
        unknowns: Object.entries(reportValues).filter(([, value]) => value === '' || value === 'unknown').map(([name]) => name)
      });
    });

    form.addEventListener('reset', () => {
      window.setTimeout(() => {
        result.hidden = true;
        result.querySelector('[data-soil-summary]').innerHTML = '';
        result.querySelector('[data-soil-actions]').innerHTML = '';
      }, 0);
    });
  }

  function openHashTarget(hash = window.location.hash) {
    if (!hash || hash.length < 2) return;
    let id = '';
    try { id = decodeURIComponent(hash.slice(1)); } catch (error) { id = hash.slice(1); }
    const target = document.getElementById(id);
    if (!target) return;
    const details = target.tagName === 'DETAILS' ? target : target.closest('details');
    if (details) details.open = true;
  }

  function initAll() {
    document.querySelectorAll('[data-aonsk-seed-framework]').forEach(initFramework);
    document.querySelectorAll('[data-aonsk-soil-v1172]').forEach(initSoil);
    openHashTarget();
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href^="#"]');
    if (!link) return;
    window.setTimeout(() => openHashTarget(link.getAttribute('href')), 0);
  });
  window.addEventListener('hashchange', () => openHashTarget());

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
})();
